import { api } from '@packages/backend/convex/_generated/api';
import { signOutApp } from '@packages/cognito-auth/react';
import { createRouter, ErrorComponent, RouterProvider } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { ActionButton } from '@/components/ActionButton';
import { LoadingIcon } from '@/components/LoadingIcon';
import { log } from '@/lib/logging';
import { routeTree } from '@/routeTree.gen';

const router = createRouter({
  // defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  // Each route catches its own render and loader errors: it shows the default error screen inside
  // the layout, and reports the error.
  defaultErrorComponent: ErrorComponent,
  defaultOnCatch: (error, errorInfo) =>
    log.error('A page crashed.', error, {
      area: 'ui',
      component_stack: errorInfo.componentStack ?? '',
    }),
  context: {
    convexUser: { sub: null, userType: null },
  },
  defaultGcTime: 0,
  defaultPendingComponent: PageLoading,
  defaultPreload: false,
  routeTree,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const AppRouter = () => {
  // const clientAuth = useAuth();
  const convexUserQuery = useQuery(api.auth.getCurrentUser);
  const posthog = usePostHog();

  // Identify the user with PostHog
  useEffect(() => {
    if (convexUserQuery.data?.sub) {
      posthog.identify(convexUserQuery.data.sub, { user_type: convexUserQuery.data.userType });
    }
  }, [convexUserQuery.data?.sub]);

  // Wait for auth to initialise before rendering the router
  if (convexUserQuery.isPending) return <PageLoading />;

  if (convexUserQuery.isError) {
    console.error(convexUserQuery.error);
    return (
      <div className={'p-4 grid gap-4'}>
        <div>There was an error loading the user.</div>
        <ActionButton className="w-[120px]" onClick={() => signOutApp()}>
          Sign out
        </ActionButton>
      </div>
    );
  }

  const convexUser = convexUserQuery.data;

  // Remount when the user or their restaurants change
  const routerKey =
    convexUser.userType === 'restaurantUser'
      ? `${convexUser.sub}:${convexUser.restaurantUser.restaurantIds.join(',')}`
      : convexUser.sub;

  return <RouterProvider key={routerKey} context={{ convexUser }} router={router} />;
};

function PageLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingIcon />
    </div>
  );
}
